/**
 * ErrorScreen — friendly failure view shown after a timeout or generic
 * error from the Roast_Generator.
 *
 * Distinguishes the two surfaces via `state.kind2`:
 * - `'timeout'`: "The jokes are taking too long." / "Let's try again."
 * - `'error'`:   "Our roast chef got distracted." / "One more swing?"
 *
 * Offers a primary Retry (replays the stored `Company_Input` byte-for-byte)
 * and a secondary Start Over that dispatches RESET.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Error Handling"
 * and Requirements 2.9, 2.10.
 */

import type { AppController } from '../state/appController';
import type { AppState } from '../state/stateMachine';

type ErrorState = Extract<AppState, { kind: 'error' }>;

/**
 * Mount the error screen into `root`. Returns a teardown function that
 * detaches listeners and removes the DOM. Safe to call twice.
 */
export function mountErrorScreen(
  root: HTMLElement,
  controller: AppController,
  state: ErrorState,
): () => void {
  const shell = document.createElement('div');
  shell.className = 'roast-shell error-screen';
  shell.setAttribute('role', 'alert');

  const title = document.createElement('h2');
  const subtitle = document.createElement('p');

  if (state.kind2 === 'timeout') {
    title.textContent = 'The jokes are taking too long.';
    subtitle.textContent = "Let's try again.";
  } else {
    title.textContent = 'Our roast chef got distracted.';
    subtitle.textContent = 'One more swing?';
  }

  // Button row
  const actions = document.createElement('div');
  actions.className = 'error-actions';

  const retryButton = document.createElement('button');
  retryButton.type = 'button';
  retryButton.textContent = 'Retry';

  const startOverButton = document.createElement('button');
  startOverButton.type = 'button';
  startOverButton.className = 'link-button';
  startOverButton.textContent = 'Start over';

  const onRetry = (): void => {
    controller.retry();
  };
  const onStartOver = (): void => {
    controller.reset();
  };

  retryButton.addEventListener('click', onRetry);
  startOverButton.addEventListener('click', onStartOver);

  actions.appendChild(retryButton);
  actions.appendChild(startOverButton);

  shell.appendChild(title);
  shell.appendChild(subtitle);
  shell.appendChild(actions);
  root.appendChild(shell);

  let torn = false;

  return () => {
    if (torn) {
      return;
    }
    torn = true;
    retryButton.removeEventListener('click', onRetry);
    startOverButton.removeEventListener('click', onStartOver);
    shell.remove();
  };
}
